// src/pages/MiniDatabase.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";

import {
  Database,
  Table2,
  RefreshCcw,
  Edit3,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Save,
  X,
  LogOut,
  ArrowLeft,
  PlayCircle,
} from "lucide-react";

const API = "https://supremeadmin.nissi-india.com";

function useTenantId() {
  const q = new URLSearchParams(useLocation().search);
  return Number(q.get("tid") || 0);
}

export default function MiniDatabase() {
  const navigate = useNavigate();
  const tenantId = useTenantId();

  // Splash gate -> workspace
  const [entered, setEntered] = useState(false);

  const [tables, setTables] = useState([]);
  const [dbName, setDbName] = useState("");
  const [selectedTable, setSelectedTable] = useState("");
  const [columns, setColumns] = useState([]);
  const [pkCols, setPkCols] = useState([]);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [orderBy, setOrderBy] = useState("");
  const [orderDir, setOrderDir] = useState("asc");
  const [loading, setLoading] = useState(false);

  const [checked, setChecked] = useState(new Set()); // key = JSON.stringify(pkObj)
  const [editRow, setEditRow] = useState(null);       // { pk, data }

  const pkKey = (row) =>
    JSON.stringify(Object.fromEntries(pkCols.map((c) => [c, row[c]])));

  // load tables
  useEffect(() => {
    (async () => {
      if (!tenantId) {
        Swal.fire("Missing tenant", "Provide ?tid=… in URL", "warning");
        return;
      }
      try {
        const r = await axios.get(`${API}/api/tenant/mysql/tables`, {
          params: { tenantId },
        });
        setTables(r.data?.tables || []);
        setDbName(r.data?.db || "");
        if (r.data?.tables?.[0]) setSelectedTable(r.data.tables[0].table);
      } catch (e) {
        Swal.fire("Error", e?.response?.data?.error || e.message, "error");
      }
    })();
  }, [tenantId]);

  // load meta & rows for selectedTable/page
  useEffect(() => {
    if (!selectedTable) return;
    (async () => {
      setLoading(true);
      setChecked(new Set());
      try {
        // 1) fetch meta
        const m = await axios.get(
          `${API}/api/tenant/mysql/table/${encodeURIComponent(selectedTable)}/meta`,
          { params: { tenantId } }
        );
        const cols = m.data?.columns || [];
        const pk = m.data?.primaryKey || [];
        setColumns(cols);
        setPkCols(pk);

        // 2) set orderBy (valid for table)
        const currentOrder = orderBy;
        const colNames = cols.map((c) => c.name);
        let nextOrderBy = currentOrder && colNames.includes(currentOrder)
          ? currentOrder
          : pk[0] || colNames[0] || "";
        if (nextOrderBy !== currentOrder) setOrderBy(nextOrderBy);

        // 3) fetch rows
        const r = await axios.get(
          `${API}/api/tenant/mysql/table/${encodeURIComponent(selectedTable)}/rows`,
          {
            params: {
              tenantId,
              page,
              pageSize,
              orderBy: nextOrderBy || undefined,
              orderDir,
            },
          }
        );
        setRows(r.data?.rows || []);
        setTotal(r.data?.total || 0);
      } catch (e) {
        Swal.fire("Error", e?.response?.data?.error || e.message, "error");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTable, page, orderDir]); // orderBy chosen inside

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const toggleCheck = (row) => {
    const k = pkKey(row);
    const next = new Set(checked);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    setChecked(next);
  };
  const selectAllPage = () => {
    const next = new Set(checked);
    rows.forEach((r) => next.add(pkKey(r)));
    setChecked(next);
  };
  const clearSelection = () => setChecked(new Set());

  const onEdit = (row) => {
    const pk = Object.fromEntries(pkCols.map((c) => [c, row[c]]));
    setEditRow({ pk, data: { ...row } });
  };
  const closeEdit = () => setEditRow(null);

  const onSaveEdit = async () => {
    if (!editRow) return;
    const updates = { ...editRow.data };
    pkCols.forEach((c) => delete updates[c]); // lock PK fields
    try {
      await axios.patch(
        `${API}/api/tenant/mysql/table/${encodeURIComponent(selectedTable)}/row`,
        { pk: editRow.pk, updates },
        { params: { tenantId } }
      );
      Swal.fire("Saved", "Row updated.", "success");
      closeEdit();
      // refresh
      const r = await axios.get(
        `${API}/api/tenant/mysql/table/${encodeURIComponent(selectedTable)}/rows`,
        {
          params: {
            tenantId,
            page,
            pageSize,
            orderBy: orderBy || undefined,
            orderDir,
          },
        }
      );
      setRows(r.data?.rows || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      Swal.fire("Error", e?.response?.data?.error || e.message, "error");
    }
  };

  const onDeleteSelected = async () => {
    if (!checked.size) return;
    const ok = await Swal.fire({
      icon: "warning",
      title: "Delete selected?",
      text: `You are about to delete ${checked.size} row(s) from ${selectedTable} in ${dbName}.`,
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#e11d48",
    }).then((r) => r.isConfirmed);
    if (!ok) return;

    try {
      const pkList = Array.from(checked).map((k) => JSON.parse(k));
      await axios.delete(
        `${API}/api/tenant/mysql/table/${encodeURIComponent(selectedTable)}/rows`,
        { params: { tenantId }, data: { pkList } }
      );
      Swal.fire("Deleted", "Selected rows removed.", "success");
      clearSelection();
      // reload
      const r = await axios.get(
        `${API}/api/tenant/mysql/table/${encodeURIComponent(selectedTable)}/rows`,
        {
          params: {
            tenantId,
            page,
            pageSize,
            orderBy: orderBy || undefined,
            orderDir,
          },
        }
      );
      setRows(r.data?.rows || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      Swal.fire("Error", e?.response?.data?.error || e.message, "error");
    }
  };

  const colNames = useMemo(() => columns.map((c) => c.name), [columns]);

  // --------------- Splash / Gate ---------------
  if (!entered) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[radial-gradient(1200px_600px_at_50%_-200px,rgba(15,23,42,0.12),transparent)] bg-slate-50">
        <div className="w-full max-w-3xl mx-auto">
          <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl p-8 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-indigo-100 blur-3xl opacity-70" />
            <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-blue-100 blur-3xl opacity-70" />

            <div className="flex items-center gap-3">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-900 text-white shadow">
                <Database className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-slate-900">
                  Mini Database Workspace
                </h1>
                <p className="text-slate-600">
                  Safe, scoped access for tenant data management.
                </p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="text-xs text-slate-500">Tenant ID</div>
                <div className="text-sm font-medium text-slate-800">
                  {tenantId || "—"}
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="text-xs text-slate-500">Database</div>
                <div className="text-sm font-medium text-slate-800">
                  {dbName || "—"}
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="text-xs text-slate-500">Tables Loaded</div>
                <div className="text-sm font-medium text-slate-800">
                  {tables.length}
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between">
              <Link
                to="/superAdmin/admin_Menu"
                className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-slate-800 ring-1 ring-slate-200 hover:bg-slate-50"
                title="Back to Admin List"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Admin List
              </Link>

              <button
                onClick={() => setEntered(true)}
                disabled={!tenantId || !dbName}
                className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2 text-white shadow hover:bg-indigo-700 disabled:opacity-60"
                title="Enter Mini Database"
              >
                <PlayCircle className="h-5 w-5" />
                Enter Workspace
              </button>
            </div>

            <div className="mt-6 text-xs text-slate-500">
              Tip: You can edit individual rows or delete multiple rows. Primary
              key fields are locked for safety.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --------------- Workspace ---------------
  return (
    <div className="min-h-screen bg-slate-100">
      {/* Top bar */}
      <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-14 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 text-white shadow">
                <Database className="h-5 w-5" />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-900 leading-tight">
                  Mini Database — <span className="text-slate-700">{dbName || "—"}</span>
                </div>
                <div className="text-xs text-slate-500 -mt-0.5">Tenant ID: {tenantId}</div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setEntered(false)}
                className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-1.5 text-slate-800 ring-1 ring-slate-200 hover:bg-slate-50"
                title="Leave Workspace"
              >
                <LogOut className="h-4 w-4" />
                Exit
              </button>
              <Link
                to="/superAdmin/admin_Menu"
                className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-1.5 text-slate-800 ring-1 ring-slate-200 hover:bg-slate-50"
                title="Back to Admin List"
              >
                <ArrowLeft className="h-4 w-4" />
                Admin List
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Workspace grid */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-12 gap-6">
        {/* Sidebar: tables */}
        <aside className="col-span-12 md:col-span-3 rounded-2xl border border-slate-200 bg-white shadow">
          <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
            <Table2 className="h-4 w-4 text-slate-600" />
            <h3 className="text-sm font-semibold text-slate-800">Tables</h3>
            <button
              className="ml-auto inline-flex items-center gap-1 text-slate-600 hover:text-slate-900"
              title="Reload"
              onClick={async () => {
                try {
                  const r = await axios.get(`${API}/api/tenant/mysql/tables`, {
                    params: { tenantId },
                  });
                  setTables(r.data?.tables || []);
                  setDbName(r.data?.db || "");
                } catch (e) {
                  Swal.fire("Error", e?.response?.data?.error || e.message, "error");
                }
              }}
            >
              <RefreshCcw className="h-4 w-4" />
            </button>
          </div>
          <div className="max-h-[70vh] overflow-auto">
            {tables.length ? (
              <ul className="divide-y divide-slate-100">
                {tables.map((t) => (
                  <li
                    key={t.table}
                    className={`px-4 py-2 cursor-pointer ${
                      selectedTable === t.table ? "bg-indigo-50" : ""
                    }`}
                    onClick={() => {
                      setSelectedTable(t.table);
                      setPage(1);
                      setOrderBy(""); // reset stale order
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-800">{t.table}</span>
                      <span className="text-[11px] text-slate-500">
                        ~{t.rowsApprox}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-4 text-sm text-slate-500">No tables</div>
            )}
          </div>
        </aside>

        {/* Main panel */}
        <section className="col-span-12 md:col-span-9 rounded-2xl border border-slate-200 bg-white shadow overflow-hidden">
          {/* Header row */}
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div>
              <div className="text-xs text-slate-500">Table</div>
              <div className="font-semibold text-slate-900">
                {selectedTable || "—"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-900 shadow-sm hover:bg-slate-50"
                onClick={selectAllPage}
              >
                Select page
              </button>
              <button
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-900 shadow-sm hover:bg-slate-50"
                onClick={clearSelection}
              >
                Clear
              </button>
              <button
                className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-3 py-1.5 text-white shadow hover:bg-rose-700 disabled:opacity-60"
                onClick={onDeleteSelected}
                disabled={!checked.size}
              >
                <Trash2 className="h-4 w-4" /> Delete Selected
              </button>
            </div>
          </div>

          {/* Order controls */}
          <div className="px-4 py-2 flex items-center gap-2 border-b border-slate-200 bg-slate-50/60">
            <label className="text-sm text-slate-700">Order by</label>
            <select
              className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
              value={orderBy}
              onChange={(e) => {
                setOrderBy(e.target.value);
                setPage(1);
              }}
            >
              <option value="">(none)</option>
              {columns.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
            <select
              className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
              value={orderDir}
              onChange={(e) => {
                setOrderDir(e.target.value);
                setPage(1);
              }}
            >
              <option value="asc">ASC</option>
              <option value="desc">DESC</option>
            </select>
            <div className="text-xs text-slate-500 ml-auto">
              {loading ? "Loading…" : `Total: ${total}`}
            </div>
          </div>

          {/* Data grid */}
          <div className="overflow-x-auto">
            <table className="min-w-[720px] w-full text-sm">
              <thead className="bg-white">
                <tr className="text-left">
                  <th className="px-3 py-2 w-10"></th>
                  {columns.map((c) => (
                    <th
                      key={c.name}
                      className="px-3 py-2 font-medium border-b border-slate-200"
                    >
                      {c.name}
                    </th>
                  ))}
                  <th className="px-3 py-2 w-24 border-b border-slate-200">Edit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r, idx) => (
                  <tr key={idx} className="odd:bg-white even:bg-slate-50">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={checked.has(pkKey(r))}
                        onChange={() => toggleCheck(r)}
                      />
                    </td>
                    {columns.map((c) => (
                      <td key={c.name} className="px-3 py-2 text-slate-800">
                        {String(r[c.name] ?? "")}
                      </td>
                    ))}
                    <td className="px-3 py-2">
                      <button
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-900 shadow-sm hover:bg-slate-50"
                        onClick={() => onEdit(r)}
                      >
                        <Edit3 className="h-4 w-4" /> Edit
                      </button>
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <td
                      colSpan={columns.length + 2}
                      className="px-4 py-8 text-center text-slate-500"
                    >
                      No rows
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 flex items-center justify-center gap-2 border-t border-slate-200 bg-white">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-900 shadow-sm disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" /> Prev
            </button>
            <span className="text-slate-700 text-sm">
              Page {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-900 shadow-sm disabled:opacity-50"
            >
              Next <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </section>
      </div>

      {/* Edit Modal */}
      {editRow && (
        <div className="fixed inset-0 bg-black/30 z-40 flex items-center justify-center">
          <div className="w-[760px] max-h-[85vh] overflow-auto rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div className="font-semibold text-slate-900">
                Edit row — {selectedTable}
              </div>
              <button
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5"
                onClick={closeEdit}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-4 grid grid-cols-2 gap-3">
              {columns.map((col) => {
                const name = col.name;
                const isPk = pkCols.includes(name);
                const value = editRow.data[name];
                return (
                  <div key={name} className="col-span-2 md:col-span-1">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {name}
                      {isPk ? " (PK)" : ""}
                    </label>
                    <input
                      className={`w-full rounded-lg border px-3 py-2 text-slate-900 ${
                        isPk ? "bg-slate-100 border-slate-200" : "border-slate-300"
                      }`}
                      value={value ?? ""}
                      onChange={(e) => {
                        if (isPk) return;
                        setEditRow((er) => ({
                          ...er,
                          data: { ...er.data, [name]: e.target.value },
                        }));
                      }}
                      disabled={isPk}
                    />
                  </div>
                );
              })}
            </div>

            <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
              <button
                className="rounded-lg border border-slate-300 bg-white px-4 py-2"
                onClick={closeEdit}
              >
                Cancel
              </button>
              <button
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-white shadow hover:bg-black"
                onClick={onSaveEdit}
              >
                <Save className="h-4 w-4" /> Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
